import React, {
    memo,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useReducer,
    useState,
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useTheme } from '@Sage/hoi-poi-ui';
import { useNavigate, useParams } from 'react-router-dom';
import { CAMPAIGNS_MARKETING } from 'constants/Entities';
import { EntityCrudService } from 'services';
import { listSenders } from 'services/SenderService';
import Context from 'managers/Context';
import { getLiteral } from 'utils/getLiteral';
import { errorToast } from 'utils/toast';
import CampaignDetailsHeader from './CampaignDetailsHeader';
import CampaignDetailsFormContent from './CampaignDetailsFormContent';
import HtmlEditorOverlay from './DesignForm/HtmlEditorOverlay';
import SenderFormModal from '../SenderFormModal';
import CampaignPreviewTestModal from './CampaignPreviewTestModal';
import {
    getMarketingCampaignStatusBadgeType,
    getMarketingCampaignStatusLabel,
    MARKETING_CAMPAIGN_STATUS,
    normalizeMarketingCampaignStatusId,
} from '../utils/marketingCampaignStatus';
import {
    normalizeMarketingSendersResponse,
    parseCampaignSenderIdField,
} from '../utils/marketingSenderOptions';

import './styles.scss';

/** Estado inicial hasta `HYDRATE_FROM_SERVER` (ruta edit) o datos del usuario (ruta new). */
const EMPTY_NEW_CAMPAIGN = {
    id: null,
    name: '',
    senderId: null,
    subject: '',
    body: '',
    status: MARKETING_CAMPAIGN_STATUS.DRAFT,
};

const MOCK_RECIPIENTS_USAGE = {
    selectedRecipients: 0,
    maxRecipients: 20000,
    remainingEmails: 20000,
};

const DEFAULT_EXPANDED_SECTIONS = {
    sender: false,
    recipients: false,
    subject: false,
    design: false,
    additionalSettings: false,
};

const showGenericBackendErrorToast = () => {
    errorToast({ text: getLiteral('error_generalerror') });
};

function comparablePersistId(value) {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    const numericValue = Number(value);
    return Number.isNaN(numericValue) ? String(value) : numericValue;
}

function buildLastPersistedFromCampaignFields(source) {
    const senderParsed = parseCampaignSenderIdField(source.senderId);
    const rawId = senderParsed.id;
    return {
        name: (source.name || '').trim(),
        senderId:
            rawId === null || rawId === undefined || rawId === ''
                ? null
                : comparablePersistId(rawId),
        subject: (source.subject || '').trim(),
        body: source.body || '',
        status: normalizeMarketingCampaignStatusId(source.status),
        previewText: (source.previewText || '').trim(),
    };
}

function isPersistedCampaignPayloadUnchanged(payload, last) {
    if (!last) {
        return false;
    }
    const nameA = (payload.name || '').trim();
    const nameB = (last.name || '').trim();
    const subA = (payload.subject || '').trim();
    const subB = (last.subject || '').trim();
    const bodyA = payload.body || '';
    const bodyB = last.body || '';
    const statusA = normalizeMarketingCampaignStatusId(payload.status);
    const statusB = normalizeMarketingCampaignStatusId(last.status);
    return (
        nameA === nameB &&
        comparablePersistId(payload.senderId) === comparablePersistId(last.senderId) &&
        subA === subB &&
        bodyA === bodyB &&
        String(statusA) === String(statusB)
    );
}

const createInitialState = (campaign, routeCampaignId, initialRecipientsUsage) => {
    const senderParsed = parseCampaignSenderIdField(campaign.senderId);
    return {
        campaignTitle: campaign.name || '',
        campaignHtml: campaign.body || '',
        campaignId: campaign.id ?? routeCampaignId ?? null,
        expandedSections: { ...DEFAULT_EXPANDED_SECTIONS },
        isHtmlEditorOpen: false,
        recipientsUsage: initialRecipientsUsage,
        savedRecipientsUsage: initialRecipientsUsage,
        isSenderModalOpen: false,
        isPreviewModalOpen: false,
        previewCampaign: null,
        senders: [],
        selectedSenderId:
            senderParsed.id !== null && senderParsed.id !== undefined && senderParsed.id !== ''
                ? String(senderParsed.id)
                : '',
        senderEmail: campaign.senderEmail || senderParsed.label || '',
        subject: campaign.subject || '',
        previewText: campaign.previewText || '',
        campaignStatusValue: normalizeMarketingCampaignStatusId(campaign.status),
        lastPersisted: buildLastPersistedFromCampaignFields(campaign),
    };
};

function campaignDetailsReducer(state, action) {
    switch (action.type) {
        case 'HYDRATE_FROM_SERVER': {
            const r = action.payload;
            const senderParsed = parseCampaignSenderIdField(r.senderId);
            return {
                ...state,
                campaignTitle: r.name || '',
                campaignHtml: r.body || '',
                campaignId: r.id ?? action.routeCampaignId,
                selectedSenderId:
                    senderParsed.id !== null &&
                    senderParsed.id !== undefined &&
                    senderParsed.id !== ''
                        ? String(senderParsed.id)
                        : '',
                senderEmail: r.senderEmail || senderParsed.label || '',
                subject: r.subject || '',
                previewText: r.previewText || '',
                campaignStatusValue: normalizeMarketingCampaignStatusId(r.status),
                lastPersisted: buildLastPersistedFromCampaignFields(r),
            };
        }
        case 'SET_LAST_PERSISTED_CAMPAIGN':
            return { ...state, lastPersisted: action.payload };
        case 'SET_CAMPAIGN_ID_FROM_ROUTE':
            return { ...state, campaignId: action.payload };
        case 'SET_RECIPIENTS_FROM_PROPS':
            return {
                ...state,
                recipientsUsage: action.payload,
                savedRecipientsUsage: action.payload,
            };
        case 'PERSIST_METADATA': {
            const { savedId, status } = action.payload;
            let next = { ...state };
            if (savedId && String(savedId) !== String(state.campaignId)) {
                next = { ...next, campaignId: savedId };
            }
            if (status !== undefined && status !== null) {
                next = {
                    ...next,
                    campaignStatusValue: normalizeMarketingCampaignStatusId(status),
                };
            }
            return next;
        }
        case 'TOGGLE_SECTION':
            return {
                ...state,
                expandedSections: {
                    ...state.expandedSections,
                    [action.payload]: !state.expandedSections[action.payload],
                },
            };
        case 'OPEN_HTML_EDITOR':
            return { ...state, isHtmlEditorOpen: true };
        case 'CLOSE_HTML_EDITOR':
            return { ...state, isHtmlEditorOpen: false };
        case 'ADD_MOCK_RECIPIENTS':
            return {
                ...state,
                recipientsUsage: {
                    ...state.recipientsUsage,
                    selectedRecipients: state.recipientsUsage.selectedRecipients + 1200,
                    remainingEmails: Math.max(0, state.recipientsUsage.remainingEmails - 1200),
                },
            };
        case 'RESET_RECIPIENTS_TO_SAVED':
            return {
                ...state,
                recipientsUsage: state.savedRecipientsUsage,
                expandedSections: { ...state.expandedSections, recipients: false },
            };
        case 'COMMIT_RECIPIENTS_SAVE':
            return {
                ...state,
                savedRecipientsUsage: state.recipientsUsage,
                expandedSections: { ...state.expandedSections, recipients: false },
            };
        case 'SET_SUBJECT':
            return { ...state, subject: action.payload };
        case 'SET_PREVIEW_TEXT':
            return { ...state, previewText: action.payload };
        case 'SET_CAMPAIGN_TITLE':
            return { ...state, campaignTitle: action.payload };
        case 'SET_TITLE_AND_HTML':
            return {
                ...state,
                campaignTitle: action.payload.title,
                campaignHtml: action.payload.html,
            };
        case 'OPEN_PREVIEW_MODAL':
            return {
                ...state,
                previewCampaign: action.payload,
                isPreviewModalOpen: true,
            };
        case 'CLOSE_PREVIEW_MODAL':
            return {
                ...state,
                isPreviewModalOpen: false,
                previewCampaign: null,
            };
        case 'OPEN_SENDER_MODAL':
            return { ...state, isSenderModalOpen: true };
        case 'CLOSE_SENDER_MODAL':
            return { ...state, isSenderModalOpen: false };
        case 'SET_SENDERS': {
            const nextSenders = action.payload;
            let nextSelectedId = state.selectedSenderId;
            const validIds = new Set(nextSenders.map((s) => String(s.value)));
            const currentValid = nextSelectedId && validIds.has(String(nextSelectedId));
            if (!currentValid) {
                const def = nextSenders.find((s) => s.isDefault) || nextSenders[0];
                nextSelectedId = def ? String(def.value) : '';
            }
            return { ...state, senders: nextSenders, selectedSenderId: nextSelectedId };
        }
        case 'APPEND_SENDER_AND_SELECT':
            return {
                ...state,
                senders: [...state.senders, action.payload.newSender],
                selectedSenderId: action.payload.newSender.value,
                isSenderModalOpen: false,
            };
        case 'SET_SELECTED_SENDER_ID':
            return { ...state, selectedSenderId: action.payload };
        case 'RECONCILE_SENDER_SELECTION': {
            const nextSenders = state.senders;
            if (!nextSenders.length) {
                return state;
            }
            const validIds = new Set(nextSenders.map((s) => String(s.value)));
            const currentValid =
                state.selectedSenderId && validIds.has(String(state.selectedSenderId));
            if (currentValid) {
                return state;
            }
            const def = nextSenders.find((s) => s.isDefault) || nextSenders[0];
            const nextSelectedId = def ? String(def.value) : '';
            if (nextSelectedId === state.selectedSenderId) {
                return state;
            }
            return { ...state, selectedSenderId: nextSelectedId };
        }
        case 'SAVE_SENDER_FIELDS':
            return {
                ...state,
                selectedSenderId: action.payload.senderId
                    ? String(action.payload.senderId)
                    : state.selectedSenderId,
            };
        default:
            return state;
    }
}

const CampaignDetails = memo(
    ({
        recipientsUsage: initialRecipientsUsage = MOCK_RECIPIENTS_USAGE,
        onBack,
        onPreview,
        onSend,
        onSendTestEmail,
        onAddRecipients,
        onCancelRecipients,
        onSaveRecipients,
    }) => {
        const theme = useTheme();
        const navigate = useNavigate();
        const { id: routeCampaignId } = useParams();

        const [state, dispatch] = useReducer(
            campaignDetailsReducer,
            { routeCampaignId, initialRecipientsUsage },
            ({ routeCampaignId: rid, initialRecipientsUsage: ru }) =>
                createInitialState(EMPTY_NEW_CAMPAIGN, rid ?? null, ru),
        );

        const [editBootstrapComplete, setEditBootstrapComplete] = useState(() => !routeCampaignId);
        const [sendersLoadComplete, setSendersLoadComplete] = useState(false);
        const [senderSelectInstanceKey, setSenderSelectInstanceKey] = useState(0);

        const {
            campaignTitle,
            campaignHtml,
            campaignId,
            expandedSections,
            isHtmlEditorOpen,
            recipientsUsage,
            isSenderModalOpen,
            isPreviewModalOpen,
            previewCampaign,
            senders,
            selectedSenderId,
            senderEmail,
            subject,
            previewText,
            campaignStatusValue,
            lastPersisted,
        } = state;

        // Do not treat auto-selected sender (SET_SENDERS default) as a local draft: it would skip
        // and abort the edit fetch when senders load before the campaign GET completes.
        const hasLocalDraft = useMemo(
            () =>
                Boolean(campaignTitle?.trim?.()) ||
                Boolean(campaignHtml?.trim?.()) ||
                Boolean(subject?.trim?.()) ||
                Boolean(previewText?.trim?.()),
            [campaignHtml, campaignTitle, previewText, subject],
        );

        useLayoutEffect(() => {
            if (!routeCampaignId) {
                setEditBootstrapComplete(true);
                return;
            }
            if (hasLocalDraft) {
                setEditBootstrapComplete(true);
                return;
            }
            setEditBootstrapComplete(false);
        }, [hasLocalDraft, routeCampaignId]);

        useEffect(() => {
            if (!routeCampaignId || hasLocalDraft) {
                return undefined;
            }

            let isCancelled = false;

            EntityCrudService.get(CAMPAIGNS_MARKETING, routeCampaignId)
                .then((result) => {
                    if (isCancelled) {
                        return;
                    }
                    if (!result) {
                        showGenericBackendErrorToast();
                        return;
                    }

                    dispatch({
                        type: 'HYDRATE_FROM_SERVER',
                        payload: result,
                        routeCampaignId,
                    });
                })
                .catch(() => {
                    if (!isCancelled) {
                        showGenericBackendErrorToast();
                    }
                })
                .finally(() => {
                    if (!isCancelled) {
                        setEditBootstrapComplete(true);
                    }
                });

            return () => {
                isCancelled = true;
            };
        }, [hasLocalDraft, routeCampaignId]);

        useEffect(() => {
            if (routeCampaignId && String(routeCampaignId) !== String(campaignId)) {
                dispatch({ type: 'SET_CAMPAIGN_ID_FROM_ROUTE', payload: routeCampaignId });
            }
        }, [campaignId, routeCampaignId]);

        useEffect(() => {
            dispatch({ type: 'SET_RECIPIENTS_FROM_PROPS', payload: initialRecipientsUsage });
        }, [initialRecipientsUsage]);

        useEffect(() => {
            if (!senders.length) {
                return;
            }
            dispatch({ type: 'RECONCILE_SENDER_SELECTION' });
        }, [senders, selectedSenderId]);

        useEffect(() => {
            let isCancelled = false;

            setSendersLoadComplete(false);

            listSenders()
                .then((data) => {
                    if (isCancelled) {
                        return;
                    }
                    const options = normalizeMarketingSendersResponse(data);
                    dispatch({ type: 'SET_SENDERS', payload: options });
                })
                .catch(() => {
                    if (!isCancelled) {
                        showGenericBackendErrorToast();
                    }
                })
                .finally(() => {
                    if (!isCancelled) {
                        setSendersLoadComplete(true);
                    }
                });

            return () => {
                isCancelled = true;
            };
        }, []);

        const normalizeIdValue = useCallback((value) => {
            if (value === null || value === undefined || value === '') {
                return null;
            }

            const numericValue = Number(value);
            return Number.isNaN(numericValue) ? value : numericValue;
        }, []);

        const getSavedCampaignId = useCallback(
            (result) => {
                const savedId = result?.id;

                return normalizeIdValue(savedId);
            },
            [normalizeIdValue],
        );

        const buildCampaignPayload = useCallback(
            (overrides = {}) => {
                const nextSenderId = overrides.senderId ?? selectedSenderId;
                const nextName = overrides.name ?? campaignTitle;
                const nextSubject = overrides.subject ?? subject;

                return {
                    id: normalizeIdValue(overrides.id ?? campaignId),
                    name: typeof nextName === 'string' ? nextName.trim() : nextName,
                    senderId: normalizeIdValue(nextSenderId),
                    subject: typeof nextSubject === 'string' ? nextSubject.trim() : nextSubject,
                    body: overrides.body ?? campaignHtml,
                    status: normalizeMarketingCampaignStatusId(
                        overrides.status ?? campaignStatusValue,
                    ),
                };
            },
            [
                campaignHtml,
                campaignId,
                campaignStatusValue,
                campaignTitle,
                normalizeIdValue,
                selectedSenderId,
                subject,
            ],
        );

        const persistCampaign = useCallback(
            async (overrides = {}) => {
                try {
                    const payload = buildCampaignPayload(overrides);
                    const hasCampaignId = !!payload.id;
                    const requestPayload = hasCampaignId
                        ? payload
                        : (({ id, ...rest }) => rest)(payload);
                    const saveAction = hasCampaignId
                        ? EntityCrudService.update
                        : EntityCrudService.create;
                    const result = await saveAction(CAMPAIGNS_MARKETING, requestPayload);
                    const savedId = getSavedCampaignId(result) || payload.id;

                    if (!hasCampaignId && !savedId) {
                        throw new Error('Marketing campaign create returned no id');
                    }

                    dispatch({
                        type: 'PERSIST_METADATA',
                        payload: {
                            savedId,
                            status: result?.status,
                        },
                    });

                    dispatch({
                        type: 'SET_LAST_PERSISTED_CAMPAIGN',
                        payload: buildLastPersistedFromCampaignFields({
                            name: payload.name,
                            senderId: payload.senderId,
                            subject: payload.subject,
                            body: payload.body,
                            status: result?.status ?? payload.status,
                            previewText,
                        }),
                    });

                    if (!hasCampaignId && savedId) {
                        navigate(`${CAMPAIGNS_MARKETING.route}/edit/${savedId}`, { replace: true });
                    }

                    return {
                        ...payload,
                        id: savedId || payload.id,
                    };
                } catch (error) {
                    showGenericBackendErrorToast();
                    throw error;
                }
            },
            [buildCampaignPayload, getSavedCampaignId, navigate, previewText],
        );

        const hasExpandedSection = useMemo(() => {
            return Object.values(expandedSections).some(Boolean);
        }, [expandedSections]);
        const loggedUserEmail = useMemo(() => {
            return Context.store.getState()?.config?.userData?.email || '';
        }, []);
        const selectedSenderOption = useMemo(() => {
            return senders.find((sender) => String(sender.value) === String(selectedSenderId));
        }, [selectedSenderId, senders]);

        const selectedSenderEmail = useMemo(() => {
            return selectedSenderOption?.label || senderEmail || '';
        }, [selectedSenderOption, senderEmail]);

        const senderDisplayName = useMemo(() => {
            return (selectedSenderOption?.subLabel || '').trim();
        }, [selectedSenderOption]);

        const currentCampaign = useMemo(
            () => ({
                id: campaignId,
                name: campaignTitle,
                senderId: normalizeIdValue(selectedSenderId),
                subject,
                body: campaignHtml,
                status: campaignStatusValue,
            }),
            [
                campaignHtml,
                campaignTitle,
                campaignId,
                campaignStatusValue,
                normalizeIdValue,
                selectedSenderId,
                subject,
            ],
        );

        const completedSections = useMemo(
            () => ({
                sender: Boolean(selectedSenderId),
                recipients: recipientsUsage.selectedRecipients > 0,
                subject: subject.trim().length > 0,
                design: campaignHtml?.trim?.().length > 0,
                additionalSettings: false,
            }),
            [campaignHtml, recipientsUsage.selectedRecipients, selectedSenderId, subject],
        );

        const handleToggleSection = useCallback((section) => {
            dispatch({ type: 'TOGGLE_SECTION', payload: section });
        }, []);

        const handleBack = useCallback(() => {
            if (onBack) {
                onBack();
                return;
            }

            navigate(CAMPAIGNS_MARKETING.route);
        }, [navigate, onBack]);

        const openPreviewModal = useCallback(
            (nextCampaign) => {
                dispatch({ type: 'OPEN_PREVIEW_MODAL', payload: nextCampaign });
                onPreview?.(nextCampaign);
            },
            [onPreview],
        );

        const handlePreview = useCallback(() => {
            openPreviewModal(currentCampaign);
        }, [currentCampaign, openPreviewModal]);

        const handleHtmlEditorPreview = useCallback(
            (previewData = {}) => {
                const nextCampaign = {
                    ...currentCampaign,
                    ...previewData,
                    body: previewData.body ?? currentCampaign.body,
                };

                openPreviewModal(nextCampaign);
            },
            [currentCampaign, openPreviewModal],
        );

        const handleClosePreviewModal = useCallback(() => {
            dispatch({ type: 'CLOSE_PREVIEW_MODAL' });
        }, []);

        const handleSendTestEmail = useCallback(
            (recipient) => {
                onSendTestEmail?.(recipient);
            },
            [onSendTestEmail],
        );

        const handleSend = useCallback(() => {
            onSend?.(currentCampaign);
        }, [currentCampaign, onSend]);

        const handleAddRecipients = useCallback(() => {
            dispatch({ type: 'ADD_MOCK_RECIPIENTS' });
            onAddRecipients?.(currentCampaign);
        }, [currentCampaign, onAddRecipients]);

        const handleCancelRecipients = useCallback(() => {
            dispatch({ type: 'RESET_RECIPIENTS_TO_SAVED' });
            onCancelRecipients?.(currentCampaign);
        }, [currentCampaign, onCancelRecipients]);

        const handleSaveRecipients = useCallback(async () => {
            try {
                dispatch({ type: 'COMMIT_RECIPIENTS_SAVE' });
                const payload = buildCampaignPayload();
                const skipApi =
                    campaignId &&
                    lastPersisted &&
                    isPersistedCampaignPayloadUnchanged(payload, lastPersisted);
                if (!skipApi) {
                    await persistCampaign();
                }
                onSaveRecipients?.(currentCampaign);
            } catch {
                /* toast: showGenericBackendErrorToast en persistCampaign */
            }
        }, [
            buildCampaignPayload,
            campaignId,
            currentCampaign,
            lastPersisted,
            onSaveRecipients,
            persistCampaign,
        ]);

        const handleSubjectChange = useCallback((value) => {
            dispatch({ type: 'SET_SUBJECT', payload: value });
        }, []);

        const handlePreviewTextChange = useCallback((value) => {
            dispatch({ type: 'SET_PREVIEW_TEXT', payload: value });
        }, []);

        const handleSaveSubject = useCallback(async () => {
            try {
                const subjectUnchanged =
                    campaignId &&
                    lastPersisted &&
                    subject.trim() === (lastPersisted.subject || '').trim() &&
                    (previewText || '').trim() === (lastPersisted.previewText || '').trim();
                if (!subjectUnchanged) {
                    await persistCampaign();
                }
                handleToggleSection('subject');
            } catch {
                /* toast: showGenericBackendErrorToast en persistCampaign */
            }
        }, [campaignId, handleToggleSection, lastPersisted, persistCampaign, previewText, subject]);

        const handleTitleSave = useCallback(
            async (newTitle) => {
                try {
                    const trimmed = typeof newTitle === 'string' ? newTitle.trim() : newTitle;
                    const titleUnchanged =
                        campaignId &&
                        lastPersisted &&
                        trimmed === (lastPersisted.name || '').trim();
                    if (titleUnchanged) {
                        return;
                    }
                    dispatch({ type: 'SET_CAMPAIGN_TITLE', payload: newTitle });
                    await persistCampaign({ name: newTitle });
                } catch {
                    /* toast: showGenericBackendErrorToast en persistCampaign */
                }
            },
            [campaignId, lastPersisted, persistCampaign],
        );

        const handleDesignOptionSelect = useCallback((option) => {
            if (option?.title === 'HTML Editor') {
                dispatch({ type: 'OPEN_HTML_EDITOR' });
            }
        }, []);

        const handleHtmlEditorExit = useCallback((discardChanges = true) => {
            if (discardChanges) {
                console.log('Discarding HTML editor changes');
            }
            dispatch({ type: 'CLOSE_HTML_EDITOR' });
        }, []);

        const handleHtmlEditorSave = useCallback(
            async ({ html, title }) => {
                dispatch({ type: 'SET_TITLE_AND_HTML', payload: { title, html } });

                try {
                    const titleTrim = typeof title === 'string' ? title.trim() : title;
                    const designUnchanged =
                        campaignId &&
                        lastPersisted &&
                        titleTrim === (lastPersisted.name || '').trim() &&
                        (html || '') === (lastPersisted.body || '');
                    if (!designUnchanged) {
                        await persistCampaign({ name: title, body: html });
                    }
                    dispatch({ type: 'CLOSE_HTML_EDITOR' });
                } catch {
                    /* toast: showGenericBackendErrorToast en persistCampaign */
                }
            },
            [campaignId, lastPersisted, persistCampaign],
        );

        const handleSaveSender = useCallback(
            async ({ senderId }) => {
                dispatch({
                    type: 'SAVE_SENDER_FIELDS',
                    payload: { senderId },
                });

                try {
                    const senderUnchanged =
                        campaignId &&
                        lastPersisted &&
                        comparablePersistId(senderId) ===
                            comparablePersistId(lastPersisted.senderId);
                    if (!senderUnchanged) {
                        await persistCampaign({ senderId });
                    }
                    handleToggleSection('sender');
                } catch {
                    /* toast: showGenericBackendErrorToast en persistCampaign */
                }
            },
            [campaignId, handleToggleSection, lastPersisted, persistCampaign],
        );

        const handleOpenSenderModal = useCallback(() => {
            dispatch({ type: 'OPEN_SENDER_MODAL' });
        }, []);

        const handleCloseSenderModal = useCallback(() => {
            dispatch({ type: 'CLOSE_SENDER_MODAL' });
        }, []);

        const handleSenderSuccess = useCallback((senderData) => {
            const newSender = {
                label: senderData?.senderEmail || senderData?.email,
                value: senderData?.id != null ? String(senderData.id) : Date.now().toString(),
                subLabel: senderData?.name,
            };

            dispatch({
                type: 'APPEND_SENDER_AND_SELECT',
                payload: { newSender },
            });
            setSenderSelectInstanceKey((n) => n + 1);
        }, []);

        const handleSenderSelectionChange = useCallback((senderId) => {
            if (senderId === null || senderId === undefined || senderId === '') {
                return;
            }
            let next = senderId;
            if (typeof next === 'object') {
                next = next.value ?? next.id ?? next.senderId;
            }
            if (next === null || next === undefined || next === '') {
                return;
            }
            dispatch({
                type: 'SET_SELECTED_SENDER_ID',
                payload: String(next),
            });
        }, []);

        if ((routeCampaignId && !editBootstrapComplete) || !sendersLoadComplete) {
            return null;
        }

        const campaignStatusLabel = getMarketingCampaignStatusLabel(currentCampaign.status);
        const badgeType = getMarketingCampaignStatusBadgeType(currentCampaign.status);
        const shouldShowHtmlEditor = isHtmlEditorOpen;
        const containerClassName = classNames('campaign-details__container', {
            'campaign-details__container--expanded': hasExpandedSection,
            'campaign-details__container--html-editor': shouldShowHtmlEditor,
        });
        const formContent = (
            <CampaignDetailsFormContent
                completedSections={completedSections}
                expandedSections={expandedSections}
                hasExpandedSection={hasExpandedSection}
                recipientsUsage={recipientsUsage}
                senderDisplayName={senderDisplayName}
                senderEmail={selectedSenderEmail}
                senders={senders}
                selectedSenderId={selectedSenderId}
                subject={subject}
                previewText={previewText}
                theme={theme}
                onAddRecipients={handleAddRecipients}
                onCancelRecipients={handleCancelRecipients}
                onDesignOptionSelect={handleDesignOptionSelect}
                onOpenSenderModal={handleOpenSenderModal}
                onPreviewTextChange={handlePreviewTextChange}
                onSaveRecipients={handleSaveRecipients}
                onSaveSender={handleSaveSender}
                onSaveSubject={handleSaveSubject}
                onSelectedSenderChange={handleSenderSelectionChange}
                onSubjectChange={handleSubjectChange}
                onToggleSection={handleToggleSection}
                senderSelectInstanceKey={senderSelectInstanceKey}
            />
        );

        return (
            <div className="campaign-details">
                <div className="campaign-details__main">
                    <CampaignDetailsHeader
                        badgeType={badgeType}
                        campaignTitle={campaignTitle}
                        onBack={handleBack}
                        onPreview={handlePreview}
                        onSend={handleSend}
                        onTitleChange={(value) =>
                            dispatch({ type: 'SET_CAMPAIGN_TITLE', payload: value })
                        }
                        onTitleSave={handleTitleSave}
                        status={campaignStatusLabel}
                    />

                    <div className={containerClassName}>
                        <div className="campaign-details__content">{formContent}</div>
                    </div>
                </div>

                <div
                    className={classNames('campaign-details__editor-overlay', {
                        'campaign-details__editor-overlay--open': shouldShowHtmlEditor,
                    })}
                    aria-hidden={!shouldShowHtmlEditor}
                >
                    <HtmlEditorOverlay
                        campaignId={campaignId}
                        initialHtml={campaignHtml}
                        initialTitle={campaignTitle}
                        isOpen={shouldShowHtmlEditor}
                        onPreview={handleHtmlEditorPreview}
                        onSave={handleHtmlEditorSave}
                        onExitWithoutSaving={handleHtmlEditorExit}
                    />
                </div>

                <SenderFormModal
                    isOpen={isSenderModalOpen}
                    onCancel={handleCloseSenderModal}
                    onSuccess={handleSenderSuccess}
                    onRequestClose={handleCloseSenderModal}
                />
                <CampaignPreviewTestModal
                    isOpen={isPreviewModalOpen}
                    onRequestClose={handleClosePreviewModal}
                    from={selectedSenderEmail}
                    subject={subject}
                    htmlPreview={previewCampaign?.body ?? currentCampaign.body}
                    defaultRecipient={loggedUserEmail}
                    onSendTestEmail={handleSendTestEmail}
                />
            </div>
        );
    },
);

CampaignDetails.displayName = 'CampaignDetails';

CampaignDetails.propTypes = {
    recipientsUsage: PropTypes.shape({
        selectedRecipients: PropTypes.number,
        maxRecipients: PropTypes.number,
        remainingEmails: PropTypes.number,
    }),
    onBack: PropTypes.func,
    onPreview: PropTypes.func,
    onSend: PropTypes.func,
    onSendTestEmail: PropTypes.func,
    onAddRecipients: PropTypes.func,
    onCancelRecipients: PropTypes.func,
    onSaveRecipients: PropTypes.func,
};

export default CampaignDetails;
